import React, { useState } from "react";
import {
  UploadCloud,
  FileText,
  Loader2,
  AlertCircle,
  CheckCircle,
  ChevronLeft,
  ListChecks,
  Rabbit,
  Bird,
} from "lucide-react";
import axios from "axios";
import ResultsTable from "./ResultsTable";
import CONFIG from "config/config";

const API_BASE = CONFIG.FAST_API || "http://localhost:8000" + "/api/v1/lab_cost";

interface LogItem {
  timestamp: string;
  message: string;
}

interface TerminalProps {
  logs: LogItem[];
  liveText: string;
}

const Terminal: React.FC<TerminalProps> = ({ logs, liveText }) => {
  const terminalRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [logs, liveText]);

  return (
    <div className="mt-8 bg-black rounded-lg overflow-hidden font-mono text-xs md:text-sm shadow-2xl border-gray-700">
      <div className="bg-gray-900 px-4 py-2 flex items-center gap-2 border-b border-gray-800">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
        </div>
        <div className="ml-4 text-green-500 font-semibold opacity-75">OCR Stream Activity</div>
      </div>
      <div ref={terminalRef} className="p-4 h-96 overflow-y-auto bg-black text-gray-300">
        {logs.map((log, i) => (
          <div key={i} className="mb-1">
            <span className="text-blue-500 mr-2">[{log.timestamp}]</span>
            <span className="text-gray-300">{log.message}</span>
          </div>
        ))}

        {liveText && (
          <div className="mt-4 pt-4 border-t border-gray-800">
            <div className="text-green-500 font-bold mb-2">LIVE OUTPUT &gt;&gt;</div>
            <div className="text-green-400 whitespace-pre-wrap leading-relaxed animate-pulse">
              {liveText}
              <span className="inline-block w-2 h-4 bg-green-500 ml-1 animate-lab-cost-blink"></span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const UploadTab: React.FC = () => {
  const [step, setStep] = useState<string>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [vendorName, setVendorName] = useState<string>("");
  const [previewInfo, setPreviewInfo] = useState<any>(null); // { file_id, total_pages, filename }
  const [selectedPages, setSelectedPages] = useState<number[]>([]);
  const [ocrProvider, setOcrProvider] = useState<string>("local");
  const [ocrOnly, setOcrOnly] = useState<boolean>(false);
  const [currentProcessingPage, setCurrentProcessingPage] = useState<number | null>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [extractedData, setExtractedData] = useState<any>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === "application/pdf") {
      setFile(selectedFile);
      setError(null);
    } else {
      setFile(null);
      setError("Please upload a valid PDF file.");
    }
  };

  const handlePreviewUpload = async () => {
    if (!vendorName.trim()) {
      setError("Vendor Name is required.");
      return;
    }
    if (!file) {
      setError("Please select a file first.");
      return;
    }

    setLoading(true);
    setError(null);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API_BASE}/preview_upload`, formData);
      setPreviewInfo(response.data);
      const allPages = Array.from({ length: response.data.total_pages }, (_, i) => i + 1);
      setSelectedPages(allPages);
      setStep("select");
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to upload file for preview.");
    } finally {
      setLoading(false);
    }
  };

  const [logs, setLogs] = useState<LogItem[]>([]);
  const [liveText, setLiveText] = useState<string>("");
  const [isStreaming, setIsStreaming] = useState<boolean>(false);

  const handleProcess = async () => {
    if (selectedPages.length === 0) {
      setError("Please select at least one page.");
      return;
    }

    setLoading(true);
    setIsStreaming(true);
    setLogs([]);
    setLiveText("");
    setCurrentProcessingPage(null);
    setExtractedData({ items: [] });
    setError(null);

    let accumulatedItems: any[] = [];

    try {
      const response = await fetch(`${API_BASE}/process_upload`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          file_id: previewInfo.file_id,
          vendor_name: vendorName,
          selected_pages: selectedPages,
          provider: ocrProvider,
          ocr_only: ocrOnly,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Server error");
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("ReadableStream not supported");
      }
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const event = JSON.parse(line);

            if (event.type === "log") {
              setLogs((prev) => [
                ...prev,
                { message: event.message, timestamp: new Date().toLocaleTimeString() },
              ]);
            } else if (event.type === "progress") {
              setCurrentProcessingPage(event.page);
            } else if (event.type === "chunk" || event.type === "extraction_chunk") {
              setLiveText((prev) => prev + event.content);
            } else if (event.type === "result") {
              if (event.items && event.items.length > 0) {
                accumulatedItems = [...accumulatedItems, ...event.items];
                setExtractedData((prev: any) => ({
                  items: [...(prev?.items || []), ...event.items],
                }));
              }
            } else if (event.type === "error") {
              setError(event.message);
              setLogs((prev) => [
                ...prev,
                { message: `ERROR: ${event.message}`, timestamp: new Date().toLocaleTimeString() },
              ]);
            }
          } catch (e) {
            console.error("Error parsing JSON:", e);
          }
        }
      }

      if (accumulatedItems.length > 0) {
        setSuccess(`Processed successfully! Found ${accumulatedItems.length} items.`);
        setTimeout(() => {
          setStep("results");
        }, 2000);
      } else if (ocrOnly) {
        setSuccess("OCR Debug complete! Check logs/live text above.");
      } else {
        setError(
          "Processing completed but no data was extracted. Please check the logs below for errors.",
        );
      }
    } catch (err: any) {
      setError(err.message || "Failed to process.");
      setLogs((prev) => [
        ...prev,
        { message: `CRITICAL ERROR: ${err.message}`, timestamp: new Date().toLocaleTimeString() },
      ]);
    } finally {
      setLoading(false);
      setIsStreaming(false);
    }
  };

  const togglePage = (page: number) => {
    setSelectedPages((prev) =>
      prev.includes(page) ? prev.filter((p) => p !== page) : [...prev, page],
    );
  };

  const selectAll = () => {
    const allPages = Array.from({ length: previewInfo.total_pages }, (_, i) => i + 1);
    setSelectedPages(allPages);
  };

  const selectNone = () => setSelectedPages([]);

  const reset = () => {
    setStep("upload");
    setFile(null);
    setVendorName("");
    setPreviewInfo(null);
    setSelectedPages([]);
    setCurrentProcessingPage(null);
    setExtractedData(null);
    setError(null);
    setSuccess(null);
  };

  return (
    <div className="w-full space-y-8 animate-lab-cost-fade-in text-gray-700">
      <div className="flex items-center justify-center space-x-4 mb-8">
        {["upload", "select", "results"].map((s, i) => (
          <React.Fragment key={s}>
            <div
              className={`flex items-center ${step === s || (step === "processing" && s === "select") ? "text-pink-600" : "text-gray-400"}`}
            >
              <span
                className={`w-8 h-8 rounded-full border-2 flex items-center justify-center font-bold mr-2 ${step === s ? "border-pink-500 bg-pink-100" : "border-current"}`}
              >
                {i + 1}
              </span>
              <span className="capitalize hidden sm:inline">
                {s === "upload" ? "Upload" : s === "select" ? "Select Pages" : "Results"}
              </span>
            </div>
            {i < 2 && <div className="w-8 h-0.5 bg-gray-200 dark:bg-gray-700" />}
          </React.Fragment>
        ))}
      </div>

      <div className="w-full bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-xl border-pink-100 dark:border-gray-700 transition-all duration-300">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 dark:bg-red-900/20 dark:text-red-400 flex items-center shadow-sm rounded-r-lg">
            <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
            <span className="text-sm font-medium">{error}</span>
          </div>
        )}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 text-green-700 dark:bg-green-900/20 dark:text-green-400 flex items-center shadow-sm rounded-r-lg">
            <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0" />
            <span className="text-sm font-medium">{success}</span>
          </div>
        )}

        {step === "upload" && (
          <div className="space-y-6 animate-lab-cost-slide-in">
            <div className="text-center">
              <h2 className="text-3xl font-extrabold text-pink-600 dark:text-white mb-2 flex justify-center items-center gap-2">
                <Rabbit className="w-8 h-8 animate-bounce" /> Upload Quotation
              </h2>
              <p className="text-gray-500 dark:text-gray-400">Feed me a PDF to munch on! 🥕</p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold ml-1">Vendor Name</label>
              <input
                list="lab-cost-vendor-options"
                type="text"
                className="w-full text-white p-4 bg-gray-50 dark:bg-gray-700 rounded-xl border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 outline-none transition-all placeholder:text-gray-400"
                placeholder="Select or type vendor name (e.g. PCT-Lab)"
                value={vendorName}
                onChange={(e) => setVendorName(e.target.value)}
              />
              <datalist id="lab-cost-vendor-options">
                <option value="PCT-Lab" />
                <option value="BRIA-Lab" />
                <option value="N-Health" />
              </datalist>
            </div>

            <div className="relative group">
              <input
                id="lab-cost-file-upload"
                type="file"
                className="hidden"
                accept=".pdf"
                onChange={handleFileChange}
              />
              <label
                htmlFor="lab-cost-file-upload"
                className={`flex flex-col items-center justify-center w-full h-56 border-2 border-dashed rounded-2xl cursor-pointer transition-all duration-300
                                ${file ? "border-blue-500 bg-blue-50/50 dark:bg-blue-900/10" : "border-gray-300 dark:border-gray-600 hover:border-blue-400 hover:bg-gray-50 dark:hover:bg-gray-700"}`}
              >
                <UploadCloud
                  className={`w-12 h-12 mb-4 transition-colors ${file ? "text-blue-500" : "text-gray-400 group-hover:text-blue-400"}`}
                />
                {file ? (
                  <div className="text-center">
                    <p className="text-lg font-bold text-pink-600 dark:text-white truncate max-w-sm px-4">
                      {file.name}
                    </p>
                    <p className="text-sm text-pink-400 dark:text-pink-300 mt-1">
                      Yummy file! Click to change
                    </p>
                  </div>
                ) : (
                  <div className="text-center">
                    <p className="text-lg font-medium text-gray-700 dark:text-gray-300">
                      Drop your PDF here
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      or click to browse from files
                    </p>
                  </div>
                )}
              </label>
            </div>

            <button
              onClick={handlePreviewUpload}
              disabled={!file || !vendorName.trim() || loading}
              className="w-full py-4 bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold rounded-xl shadow-lg hover:from-pink-600 hover:to-rose-600 transform hover:-translate-y-0.5 active:translate-y-0 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-lg"
            >
              {loading ? (
                <Loader2 className="w-6 h-6 animate-spin mr-2" />
              ) : (
                <>
                  <Rabbit className="w-6 h-6 mr-1" /> Hop to Page Selection
                </>
              )}
            </button>
          </div>
        )}

        {step === "select" && previewInfo && (
          <div className="space-y-6 animate-lab-cost-slide-in">
            <div className="text-center">
              <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-2">
                Select Pages
              </h2>
              <p className="text-gray-500 dark:text-gray-400">
                Choose which pages to scan for lab tests
              </p>
            </div>

            <div className="flex justify-between items-center px-2 py-3 bg-gray-50 dark:bg-gray-700 rounded-xl mb-4">
              <span className="text-sm font-bold flex items-center">
                <ListChecks className="w-4 h-4 mr-2" /> {selectedPages.length} of{" "}
                {previewInfo.total_pages} selected
              </span>
              <div className="space-x-4">
                <button
                  onClick={selectAll}
                  className="text-sm text-blue-600 dark:text-blue-400 font-bold hover:underline"
                >
                  Select All
                </button>
                <button
                  onClick={selectNone}
                  className="text-sm text-gray-500 dark:text-gray-400 font-medium hover:underline"
                >
                  Clear
                </button>
              </div>
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
              {Array.from({ length: previewInfo.total_pages }, (_, i) => i + 1).map((p: any) => {
                const isProcessing = p === currentProcessingPage;
                const isSelected = selectedPages.includes(p);
                return (
                  <button
                    key={p}
                    onClick={() => togglePage(p)}
                    disabled={isStreaming}
                    className={`aspect-square rounded-xl border-2 flex flex-col items-center justify-center p-2 transition-all group relative
                                    ${
                                      isProcessing
                                        ? "border-orange-500 bg-orange-50 dark:bg-orange-900/20 ring-4 ring-orange-500/30 z-10 scale-105"
                                        : isSelected
                                          ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 ring-2 ring-blue-500/20"
                                          : "border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-500 text-gray-400"
                                    }`}
                  >
                    {isProcessing ? (
                      <Loader2 className="w-8 h-8 mb-1 animate-spin text-orange-500" />
                    ) : (
                      <FileText
                        className={`w-8 h-8 mb-1 transition-colors ${isSelected ? "text-blue-500" : "text-gray-300 dark:text-gray-600 group-hover:text-blue-300"}`}
                      />
                    )}
                    <span className={`text-sm font-bold ${isProcessing ? "text-orange-600" : ""}`}>
                      P. {p}
                    </span>
                    {isProcessing && (
                      <span className="absolute -top-2 -right-2 flex h-4 w-4">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-4 w-4 bg-orange-500"></span>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="flex space-x-4 pt-4">
              <button
                onClick={() => setStep("upload")}
                className="flex-1 py-4 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-bold rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-all flex items-center justify-center"
              >
                <ChevronLeft className="w-5 h-5 mr-1" /> Back
              </button>

              <div className="flex-1 flex flex-col gap-2">
                <select
                  value={ocrProvider}
                  onChange={(e) => setOcrProvider(e.target.value)}
                  className="w-full text-white p-4 bg-gray-50 dark:bg-gray-700 rounded-xl border-gray-200 dark:border-gray-600 font-semibold outline-none focus:ring-2 focus:ring-blue-500 h-14"
                >
                  {/* <option value="local">Local (DeepSeek)</option> */}
                  <option value="mistral">Mistral Cloud</option>
                </select>

                <div className="flex items-center space-x-2 px-1">
                  <input
                    type="checkbox"
                    id="lab-cost-ocr-only"
                    checked={ocrOnly}
                    onChange={(e) => setOcrOnly(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <label
                    htmlFor="lab-cost-ocr-only"
                    className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer"
                  >
                    OCR Only (Debug Mode)
                  </label>
                </div>
              </div>

              <button
                onClick={handleProcess}
                disabled={selectedPages.length === 0 || loading}
                className="flex-[2] py-4 bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-bold rounded-xl shadow-lg hover:from-purple-600 hover:to-indigo-600 transition-all flex items-center justify-center text-lg disabled:opacity-50 disabled:cursor-not-allowed h-14"
              >
                {loading && isStreaming ? (
                  <span className="flex items-center">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Streaming...
                  </span>
                ) : ocrOnly ? (
                  "Run Debug OCR"
                ) : (
                  "Extract Data Selected"
                )}
              </button>
            </div>

            {(isStreaming || logs.length > 0) && <Terminal logs={logs} liveText={liveText} />}
          </div>
        )}

        {step === "processing" && (
          <div className="py-12 flex flex-col items-center text-center space-y-6 animate-pulse">
            <div className="w-24 h-24 bg-pink-100 dark:bg-pink-900/20 rounded-full flex items-center justify-center animate-bounce">
              <Bird className="w-16 h-16 text-pink-500" />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-2 text-pink-600">Munching on Data... 🥕</h2>
              <p className="text-gray-500 max-w-xs mx-auto">
                Our bunnies are reading {selectedPages.length} pages just for you!
              </p>
            </div>
          </div>
        )}

        {step === "results" && extractedData && (
          <div className="space-y-6 animate-lab-cost-fade-in">
            <div className="flex justify-between items-center mb-4">
              <div className="text-left">
                <h2 className="text-3xl font-extrabold mb-1">Extraction Summary</h2>
                <p className="text-green-600 dark:text-green-400 font-medium">
                  Found {extractedData.items.length} items from {selectedPages.length} pages
                </p>
              </div>
              <button
                onClick={reset}
                className="px-6 py-2 border-2 border-blue-600 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 font-bold transition-all"
              >
                Start New Upload
              </button>
            </div>
            <ResultsTable data={extractedData} />
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadTab;
